import { type ComponentProps, useEffect, useRef } from "react";
import styled from "styled-components";
import { CloseButton } from "src/auth/components/CloseButton/CloseButton";
import { spacing } from "src/design-system/tokens/spacing";
import { color, fontSize, fontWeight } from "../../../theme";
import type { MergeElementProps } from "../../../utils/MergeElementProps";

type Props = MergeElementProps<
  "div",
  {
    onCloseClicked: ComponentProps<typeof CloseButton>["onClick"];
    className?: string;
  }
>;

export function Drawer2Title({
  onCloseClicked,
  className,
  children,
  ...rest
}: Props) {
  const focusRef = useRef<HTMLButtonElement>(null);

  // Improve A11Y by ensuring the focus is moved to the Drawer (close button) when it opens.
  // The Drawer2 is used on mobile, and when open it obscures the screen behind it.
  // To give all users the same experience we make sure that the focus (for screen readers and mobile-keyboard users),
  // is not stuck behind the Drawer2 component.
  useEffect(() => {
    if (focusRef?.current) {
      focusRef.current.focus();
    }
  }, [focusRef]);

  return (
    <Root noHeading={!children} {...rest}>
      {children && <Heading>{children}</Heading>}
      <CloseButtonWrapper>
        <CloseButton buttonRef={focusRef} onClick={onCloseClicked} />
      </CloseButtonWrapper>
    </Root>
  );
}

const Root = styled.div<{ noHeading: boolean }>`
  position: relative;
  ${({ noHeading }) => noHeading && "height: 40px;"}
  padding: 8px 72px;
`;

const Heading = styled.h4`
  font-size: ${fontSize.h4};
  font-weight: ${fontWeight.medium};
  text-align: center;
  color: ${color.n300};
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  padding: ${spacing.md};
`;
